import getXPath from 'get-xpath';
import PropTypes from 'prop-types';
import { useState, useCallback } from 'react';
import { BACKGROUND_ELEMENT_CLASS } from '@constants';
import { scannerItem } from '@types/scanner-item';
import { buildCSSRule, updateCSS } from '@utils/color-contrast-helpers';
import { focusOnElement, removeExistingFocus } from '@utils/focus-on-element';
import { getElementByXPath } from '@utils/get-element-by-xpath';

export const useColorContrastForm = ({
	item,
	parents,
	color,
	background,
	onUpdateFormData,
}) => {
	const [parentChanged, setParentChanged] = useState(false);

	const updateData = useCallback(
		(data) => {
			onUpdateFormData(data);

			if (data.color || data.background) {
				// Merge current values with new data to generate complete CSS rule
				const mergedData = {
					color: data.color || color,
					background: data.background || background,
					parents: data.parents || parents,
				};
				const rule = buildCSSRule(item, mergedData, parents);
				updateCSS(rule);
			}
		},
		[item, parents, color, background, onUpdateFormData],
	);

	const changeColor = useCallback(
		(newColor) => {
			updateData({ color: newColor });
		},
		[updateData],
	);

	const changeBackground = useCallback(
		(newBackground) => {
			const element = getElementByXPath(parents.at(-1));

			if (!element) {
				return;
			}

			updateData({
				background: newBackground,
				backgroundChanged: true,
			});
		},
		[parents, updateData],
	);

	const setParentLarger = useCallback(() => {
		const element = getElementByXPath(parents.at(-1));
		const parent = element?.parentElement;

		if (!element || !parent) {
			return;
		}

		try {
			const xpath = getXPath(parent, { ignoreId: true });
			focusOnElement(parent, BACKGROUND_ELEMENT_CLASS);
			updateData({
				parents: [...parents, xpath],
			});
			setParentChanged(true);
		} catch (error) {
			console.warn('Failed to get XPath for parent element: ', error);
		}
	}, [parents, updateData]);

	const setParentSmaller = useCallback(() => {
		const newParents = parents.slice(0, -1);
		const nextElement = getElementByXPath(newParents.at(-1));
		if (parents.length <= 1 || !nextElement) {
			return;
		}

		if (newParents.length > 1) {
			focusOnElement(nextElement, BACKGROUND_ELEMENT_CLASS);
		} else {
			removeExistingFocus(BACKGROUND_ELEMENT_CLASS);
		}

		updateData({ parents: newParents, resolved: false });
		setParentChanged(true);
	}, [parents, updateData]);

	return {
		parentChanged,
		changeColor,
		changeBackground,
		setParentLarger,
		setParentSmaller,
	};
};

useColorContrastForm.propTypes = {
	item: scannerItem.isRequired,
	parents: PropTypes.array,
	color: PropTypes.string,
	background: PropTypes.string,
	onUpdateFormData: PropTypes.func.isRequired,
};
